import type { ApiError, ApiResponse } from '@/types/api';
import { toast } from 'sonner';

// --- Structured Error Classes ---

export class AppError extends Error {
  code: string;
  status: number;
  isRetryable: boolean;
  userMessage: string;
  details?: Record<string, unknown>;

  constructor(opts: {
    message: string;
    code?: string;
    status?: number;
    isRetryable?: boolean;
    userMessage?: string;
    details?: Record<string, unknown>;
  }) {
    super(opts.message);
    this.name = 'AppError';
    this.code = opts.code ?? 'UNKNOWN_ERROR';
    this.status = opts.status ?? 0;
    this.isRetryable = opts.isRetryable ?? false;
    this.userMessage = opts.userMessage ?? 'Something went wrong. Please try again.';
    this.details = opts.details;
  }
}

export class NetworkError extends AppError {
  constructor(message?: string) {
    super({
      message: message ?? 'Network request failed',
      code: 'NETWORK_ERROR',
      status: 0,
      isRetryable: true,
      userMessage: 'Connection lost. Please check your internet and try again.',
    });
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends AppError {
  constructor() {
    super({
      message: 'Request timed out',
      code: 'TIMEOUT',
      status: 0,
      isRetryable: true,
      userMessage: 'The request took too long. Please try again.',
    });
    this.name = 'TimeoutError';
  }
}

export class AuthError extends AppError {
  constructor(message?: string) {
    super({
      message: message ?? 'Authentication required',
      code: 'UNAUTHORIZED',
      status: 401,
      isRetryable: false,
      userMessage: 'Your session has expired. Redirecting to sign in...',
    });
    this.name = 'AuthError';
  }
}

// --- Configuration ---

const REQUEST_TIMEOUT_MS = 10_000;
const MAX_RETRIES = 2;
const INITIAL_BACKOFF_MS = 200;

// In development, use relative URLs to leverage Vite proxy
// In production, use the configured API URL or default to production URL
const getApiUrl = (): string => {
  if (import.meta.env.DEV) {
    return '';
  }
  return import.meta.env.VITE_API_URL || 'https://api.pureluck.app';
};

// --- Helpers ---

function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get Clerk session token
 */
export async function getAuthToken(
  getToken?: () => Promise<string | null>
): Promise<string | null> {
  if (getToken) {
    return getToken();
  }
  return null;
}

// --- Response Handling ---

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');

  // Read response body once
  const text = await response.text();

  // Handle non-JSON responses
  if (!contentType?.includes('application/json')) {
    if (response.status === 404) {
      throw new AppError({
        message: 'API endpoint not found',
        code: 'NOT_FOUND',
        status: 404,
        isRetryable: false,
        userMessage: 'The requested resource was not found.',
      });
    }
    throw new AppError({
      message: `Invalid response format: ${contentType || 'unknown'} (status ${response.status})`,
      code: 'INVALID_RESPONSE',
      status: response.status,
      isRetryable: false,
      userMessage: 'Received an unexpected response from the server.',
    });
  }

  // Parse JSON response
  let data: ApiResponse<T> | ApiError;
  try {
    data = JSON.parse(text) as ApiResponse<T> | ApiError;
  } catch {
    throw new AppError({
      message: `Failed to parse JSON: ${text.substring(0, 200)}`,
      code: 'PARSE_ERROR',
      status: response.status,
      isRetryable: false,
      userMessage: 'Received a malformed response from the server.',
    });
  }

  // Handle 401 — redirect to sign-in
  if (response.status === 401) {
    const msg =
      'error' in data && data.error
        ? data.error.message
        : 'Authentication required';
    throw new AuthError(msg);
  }

  // Handle other error responses
  if (!response.ok) {
    const errorPayload =
      'error' in data && data.error
        ? data.error
        : { code: 'UNKNOWN_ERROR', message: `HTTP ${response.status}: ${response.statusText}` };

    throw new AppError({
      message: errorPayload.message,
      code: errorPayload.code,
      status: response.status,
      isRetryable: isRetryableStatus(response.status),
      userMessage: errorPayload.message,
      details: 'details' in errorPayload ? (errorPayload as { details?: Record<string, unknown> }).details : undefined,
    });
  }

  if ('error' in data && data.error) {
    throw new AppError({
      message: data.error.message,
      code: data.error.code,
      status: response.status,
      isRetryable: false,
      userMessage: data.error.message,
    });
  }

  return 'data' in data && data.data ? data.data : (data as T);
}

// --- Core Request with Retry + Timeout ---

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  getToken?: () => Promise<string | null>
): Promise<T> {
  const token = await getAuthToken(getToken);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const apiUrl = getApiUrl();
  const url = apiUrl ? `${apiUrl}${endpoint}` : endpoint;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    // Exponential backoff (skip delay on first attempt)
    if (attempt > 0) {
      const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt - 1);
      // Add jitter: ±25%
      const jitter = backoff * (0.75 + Math.random() * 0.5);
      await delay(jitter);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // For non-retryable statuses, don't retry — throw immediately
      if (!response.ok && !isRetryableStatus(response.status)) {
        return await handleResponse<T>(response);
      }

      // For retryable statuses on non-final attempt, retry
      if (!response.ok && isRetryableStatus(response.status) && attempt < MAX_RETRIES - 1) {
        lastError = new AppError({
          message: `Server error: ${response.status}`,
          code: 'SERVER_ERROR',
          status: response.status,
          isRetryable: true,
          userMessage: 'The server is temporarily unavailable. Retrying...',
        });
        continue;
      }

      return await handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);

      // Don't retry auth errors or non-retryable AppErrors
      if (error instanceof AuthError) {
        handleAuthRedirect();
        throw error;
      }

      if (error instanceof AppError && !error.isRetryable) {
        throw error;
      }

      // Handle abort (timeout)
      if (error instanceof DOMException && error.name === 'AbortError') {
        lastError = new TimeoutError();
        if (attempt < MAX_RETRIES - 1) continue;
        throw lastError;
      }

      // Handle network errors (TypeError from fetch)
      if (error instanceof TypeError) {
        lastError = new NetworkError(
          import.meta.env.DEV
            ? 'Failed to connect to backend server. Ensure the Cloudflare Worker is running on port 8788.'
            : undefined
        );
        if (attempt < MAX_RETRIES - 1) continue;
        throw lastError;
      }

      // Unknown error — don't retry
      throw error;
    }
  }

  // Should not reach here, but safety fallback
  throw lastError ?? new AppError({ message: 'Request failed after retries' });
}

// --- Auth Redirect ---

function handleAuthRedirect(): void {
  toast.error('Session expired. Redirecting to sign in...');
  // Small delay so the toast is visible before redirect
  setTimeout(() => {
    window.location.href = '/signup';
  }, 1500);
}

// --- Convenience Methods ---

export async function apiGet<T>(
  endpoint: string,
  getToken?: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' }, getToken);
}

export async function apiPost<T>(
  endpoint: string,
  body: unknown,
  getToken?: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    getToken
  );
}

export async function apiPatch<T>(
  endpoint: string,
  body: unknown,
  getToken?: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(
    endpoint,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    getToken
  );
}

export async function apiDelete<T>(
  endpoint: string,
  getToken?: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' }, getToken);
}
